/**
 * 📋 SDLC 6.3.1 UNIFIED SPECIFICATION STANDARD FILE HEADER
 * ===============================================================================
 * FILE: frontend/src/components/tenant-admin/OrgCreateSidePanel.tsx
 * PURPOSE: Slide-in side panel for inline organization creation from AnchorPicker
 *          (DD-0052 Option B). Allows tenant admins to create org tree nodes
 *          without leaving the Create User form (preserves form state per AC-UX-2).
 * SDLC FRAMEWORK VERSION: 6.3.1 (7-Pillar + 3-Section + Long-Running Agent Protocol)
 * DATE: 2026-04-29
 * COMPLIANCE: SDLC 6.3.1 + DD-0052 v0.1.0 + BR-TENM-013 v0.2.2 §UX-Journey
 * PILLAR: P1 (Stage 04 - BUILD) + P6 (UI/UX Design) + P4 (Quality Gates)
 * SPRINT GOVERNANCE: Rule 3: Zero-Mock Tolerance, Rule 9: Documentation Standards
 *
 * Per DD-0052 v0.1.0 §5.1 Component Diagram + §5.7 Mobile Responsive sketch:
 *   - Desktop: 480px slide-in side panel from right edge.
 *   - Mobile (<768px): bottom-sheet variant (translate-y) with drag-handle.
 *   - Focus trap, ESC + backdrop click → confirm-close if dirty, else close.
 *   - On submit success: invalidates ['organizations', 'tree', tenantId] query
 *     so AnchorPicker refreshes with new node, then auto-selects via onCreated.
 *
 * Risks acknowledged inline (per memory feedback_escalation_policy.md):
 *   1. Focus trap a11y — implemented with keyboard event handler + initial-focus
 *      on first input. M-UX-5 axe-core scan deferred to @tester S14.12.
 *   2. Mobile bottom-sheet swipe-down conflict with form scroll — mitigated by
 *      constraining drag handler to drag-handle area only (per DD-0052 §6.3).
 *   3. Vietnamese key collision — keys namespaced under tenant_admin.org_tree.*
 *      to avoid clash with existing translation.json structure.
 *   4. Capability fail-closed — defensive: if user lacks manage_organization
 *      permission, parent (AnchorPicker) hides the create button entirely.
 *
 * @business-context
 * - Domain: Tenant onboarding UX, multi-tenant org tree management
 * - Impact: Operational — closes BKL pilot first-user creation deadlock
 * - Stakeholders: Tenant admins (IT/CIO/COO), BKL pilot
 *
 * @technical-architecture
 * - Layer: Presentation (controlled overlay component)
 * - Pattern: Side-panel slide / bottom-sheet (responsive variant)
 * - Dependencies: React 18, TanStack Query (cache invalidation), apiClient
 *
 * @cross-references
 * - DD: docs/02-design/18-adrs/06-decisions/DD-0052-tenant-admin-org-tree-discoverability.md (§5.1, §5.4)
 * - BR: docs/01-planning/01-requirements/03-tenant-management/BR-TENM-013-USER-ANCHOR-LIFECYCLE.md (v0.2.2 AC-UX-2)
 * - Reuse: frontend/src/pages/OrganizationManagement/OrganizationCreate.tsx
 * - Hook: frontend/src/hooks/useOrganizationTree.ts (queryKey ['organizations','tree',tenantId])
 * - Consumer: frontend/src/components/tenant-admin/AnchorPicker.tsx
 */

import React, { useEffect, useRef, useState, useCallback } from "react";
import { useTranslation } from "react-i18next";
import { useQueryClient } from "@tanstack/react-query";
import { apiClient } from "../../services/api/client";
import type { OrganizationNode } from "../../hooks/useOrganizationTree";
// BA-D-010 (2026-08-04): hợp nhất với OrganizationModal (SC-006) — cùng danh mục loại,
// cùng hàm suy org_type/level, cùng pattern mã. KHÔNG nhân đôi mapping tại đây.
import {
  fetchUnitTypes,
  resolveOrgTypeLevel,
} from "../../pages/OrganizationManagement/components/OrganizationModal";

export interface OrgCreateSidePanelProps {
  /** Whether the panel is open (controlled by parent). */
  open: boolean;
  /** Pre-filled parent organization (from AnchorPicker current selection). */
  parent?: OrganizationNode | null;
  /** Tenant scope; passed through for cache invalidation. */
  tenantId?: string;
  /** Called when user closes panel (cancel/ESC/backdrop). */
  onClose: () => void;
  /** Called after successful create with the new organization id. */
  onCreated: (newOrganizationId: string) => void;
}

/** Pattern mã thống nhất với OrganizationModal/BS-006-R04 (không underscore, 2–50 ký tự). */
const CODE_PATTERN = /^[A-Z0-9-]{2,50}$/;

interface FormState {
  code: string;
  name: string;
  unitTypeCode: string;
}

/**
 * Side-panel slide-in for inline organization creation. Mounts as overlay above
 * AnchorPicker without unmounting the parent Create User form (form state
 * preservation per AC-UX-2 visual continuity).
 */
export const OrgCreateSidePanel: React.FC<OrgCreateSidePanelProps> = ({
  open,
  parent,
  tenantId,
  onClose,
  onCreated,
}) => {
  const { t } = useTranslation();
  const queryClient = useQueryClient();
  const firstInputRef = useRef<HTMLInputElement>(null);
  const panelRef = useRef<HTMLDivElement>(null);

  const [form, setForm] = useState<FormState>({
    code: "",
    name: "",
    unitTypeCode: "",
  });
  const [unitTypes, setUnitTypes] = useState<Awaited<ReturnType<typeof fetchUnitTypes>>>([]);
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [submitting, setSubmitting] = useState(false);
  const [isMobile, setIsMobile] = useState<boolean>(
    typeof window !== "undefined" && window.innerWidth < 768
  );

  // ── Effects ─────────────────────────────────────────────────────────────

  // Track viewport for mobile bottom-sheet variant (DD-0052 §5.7).
  useEffect(() => {
    const handleResize = () => setIsMobile(window.innerWidth < 768);
    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, []);

  // Focus initial input when panel opens (a11y).
  useEffect(() => {
    if (open && firstInputRef.current) {
      // Defer focus to next tick after panel mounts/transitions.
      const timer = setTimeout(() => firstInputRef.current?.focus(), 50);
      return () => clearTimeout(timer);
    }
  }, [open]);

  // Reset form whenever panel re-opens with a different parent.
  useEffect(() => {
    if (open) {
      setForm({ code: "", name: "", unitTypeCode: "" });
      setErrors({});
    }
  }, [open, parent?.id]);

  // BA-D-010: nạp danh mục Loại đơn vị (chỉ loại đang hoạt động — BS-008-R06)
  useEffect(() => {
    if (!open) return;
    fetchUnitTypes()
      .then((types) => setUnitTypes(types.filter((ut) => ut.is_active)))
      .catch(() => setUnitTypes([]));
  }, [open]);

  // ESC key → close (with dirty-confirm).
  const isDirty = !!(form.code || form.name || form.unitTypeCode);
  const handleClose = useCallback(() => {
    if (isDirty && !window.confirm(t("tenant_admin.org_tree.confirm_close"))) {
      return;
    }
    onClose();
  }, [isDirty, onClose, t]);

  useEffect(() => {
    if (!open) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") {
        e.preventDefault();
        handleClose();
      }
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [open, handleClose]);

  // Lightweight focus trap — keep tab cycle inside panel while open.
  useEffect(() => {
    if (!open) return;
    const onKeyDown = (e: KeyboardEvent) => {
      if (e.key !== "Tab" || !panelRef.current) return;
      const focusable = panelRef.current.querySelectorAll<HTMLElement>(
        'button:not([disabled]), [href], input:not([disabled]), select:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex="-1"])'
      );
      if (focusable.length === 0) return;
      const first = focusable[0];
      const last = focusable[focusable.length - 1];
      if (e.shiftKey && document.activeElement === first) {
        e.preventDefault();
        last.focus();
      } else if (!e.shiftKey && document.activeElement === last) {
        e.preventDefault();
        first.focus();
      }
    };
    window.addEventListener("keydown", onKeyDown);
    return () => window.removeEventListener("keydown", onKeyDown);
  }, [open]);

  // ── Computed ───────────────────────────────────────────────────────────

  // BA-D-010: org_type/level suy từ Loại đơn vị đã chọn + cấp cha — CÙNG hàm
  // resolveOrgTypeLevel với OrganizationModal (SC-006), không tự suy từ parent nữa.
  // Chưa chọn Loại → chưa xác định (ô Cấp hiện "—").
  const resolved = form.unitTypeCode
    ? resolveOrgTypeLevel(form.unitTypeCode, parent?.level ?? null)
    : null;

  // ── Handlers ───────────────────────────────────────────────────────────

  const validate = (): boolean => {
    const newErrors: Record<string, string> = {};
    if (!form.unitTypeCode) {
      newErrors.unitType = t("tenant_admin.org_tree.validation_unit_type_required");
    }
    if (!form.code) {
      newErrors.code = t("tenant_admin.org_tree.validation_code_required");
    } else if (!CODE_PATTERN.test(form.code)) {
      newErrors.code = t("tenant_admin.org_tree.validation_code_pattern");
    }
    if (!form.name.trim()) {
      newErrors.name = t("tenant_admin.org_tree.validation_name_required");
    }
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!validate()) return;
    setSubmitting(true);
    try {
      // Reuse POST /api/v1/organizations/ — same endpoint as full OrganizationCreate page.
      // [fix 2026-08-05] PHẢI trailing slash: POST không slash bị FastAPI 307 redirect
      // và browser không follow (CORS/proxy) ⇒ create chết với lỗi generic.
      // BA-D-010: payload khớp OrganizationModal — KHÔNG hardcode ai_enabled/solver_priority
      // (để default backend quyết); org_type/level suy từ Loại đã chọn.
      const payload = {
        code: form.code.trim().toUpperCase(),
        name: form.name.trim(),
        organization_type: resolved!.orgType,
        level: resolved!.level,
        parent_id: parent?.id ?? null,
      };
      const response = await apiClient.post("/organizations/", payload);
      const newOrg = response.data;

      // Invalidate AnchorPicker tree query so cascading dropdown refreshes
      // (per DD-0052 §5.2 state management strategy).
      await queryClient.invalidateQueries({
        queryKey: ["organizations", "tree", tenantId ?? "current"],
      });

      // Hand new id back to parent for auto-select.
      onCreated(newOrg.id);
    } catch (err: unknown) {
      // Defensive error mapping — surface duplicate-code / permission to user.
      const error = err as {
        response?: { status?: number; data?: { detail?: string } };
      };
      const status = error?.response?.status;
      const detail = error?.response?.data?.detail || "";
      let key = "tenant_admin.org_tree.error_generic";
      if (status === 409 || detail.toLowerCase().includes("duplicate")) {
        key = "tenant_admin.org_tree.error_duplicate_code";
      } else if (status === 403) {
        key = "tenant_admin.org_tree.error_permission";
      }
      setErrors({ submit: t(key) });
    } finally {
      setSubmitting(false);
    }
  };

  if (!open) return null;

  // ── Render ─────────────────────────────────────────────────────────────

  // Backdrop (click-outside → close).
  const backdropStyle: React.CSSProperties = {
    position: "fixed",
    inset: 0,
    background: "rgba(0,0,0,0.4)",
    zIndex: 100,
    transition: "opacity 0.2s ease",
  };

  // Panel container: side-panel on desktop, bottom-sheet on mobile.
  const panelStyle: React.CSSProperties = isMobile
    ? {
        position: "fixed",
        left: 0,
        right: 0,
        bottom: 0,
        maxHeight: "85vh",
        background: "white",
        borderTopLeftRadius: "16px",
        borderTopRightRadius: "16px",
        zIndex: 101,
        display: "flex",
        flexDirection: "column",
        boxShadow: "0 -4px 24px rgba(0,0,0,0.15)",
      }
    : {
        position: "fixed",
        top: 0,
        right: 0,
        bottom: 0,
        width: "480px",
        maxWidth: "100vw",
        background: "white",
        zIndex: 101,
        display: "flex",
        flexDirection: "column",
        boxShadow: "-4px 0 24px rgba(0,0,0,0.15)",
        transform: "translateX(0)",
        transition: "transform 0.2s ease",
      };

  return (
    <>
      {/* Backdrop */}
      <div
        style={backdropStyle}
        onClick={handleClose}
        aria-hidden="true"
        data-testid="org-create-side-panel-backdrop"
      />
      {/* Panel */}
      <div
        ref={panelRef}
        style={panelStyle}
        role="dialog"
        aria-modal="true"
        aria-labelledby="org-create-side-panel-title"
        data-testid="org-create-side-panel"
      >
        {/* Mobile drag handle (bottom-sheet swipe-down hint per DD-0052 §6.3 mitigation:
            drag area constrained to handle, NOT panel body, to avoid form scroll conflict). */}
        {isMobile && (
          <div
            style={{
              display: "flex",
              justifyContent: "center",
              padding: "8px 0",
              cursor: "grab",
            }}
            aria-label={t("tenant_admin.org_tree.mobile_drawer_handle")}
            data-testid="org-create-side-panel-drag-handle"
          >
            <div
              style={{
                width: "40px",
                height: "4px",
                background: "#d1d5db",
                borderRadius: "2px",
              }}
            />
          </div>
        )}

        {/* Header */}
        <div
          style={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "flex-start",
            padding: "20px 24px",
            borderBottom: "1px solid #e5e7eb",
          }}
        >
          <div>
            <h2
              id="org-create-side-panel-title"
              style={{
                fontSize: "18px",
                fontWeight: 600,
                color: "#1f2937",
                margin: 0,
              }}
            >
              {t("tenant_admin.org_tree.panel_title")}
            </h2>
            <p
              style={{
                fontSize: "13px",
                color: "#6b7280",
                margin: "4px 0 0 0",
              }}
            >
              {t("tenant_admin.org_tree.panel_description")}
            </p>
          </div>
          <button
            type="button"
            onClick={handleClose}
            aria-label={t("tenant_admin.org_tree.close_button_aria_label")}
            style={{
              background: "none",
              border: "none",
              cursor: "pointer",
              fontSize: "20px",
              color: "#6b7280",
              padding: "4px 8px",
              minHeight: "44px",
              minWidth: "44px",
            }}
          >
            ✕
          </button>
        </div>

        {/* Body */}
        <form
          onSubmit={handleSubmit}
          style={{
            flex: 1,
            overflowY: "auto",
            padding: "20px 24px",
            display: "flex",
            flexDirection: "column",
            gap: "16px",
          }}
        >
          {/* Parent (read-only display) */}
          <div>
            <label
              style={{
                display: "block",
                fontSize: "13px",
                fontWeight: 500,
                color: "#374151",
                marginBottom: "6px",
              }}
            >
              {t("tenant_admin.org_tree.field_parent")}
            </label>
            <div
              style={{
                padding: "10px 12px",
                background: "#f9fafb",
                border: "1px solid #e5e7eb",
                borderRadius: "6px",
                fontSize: "14px",
                color: parent ? "#1f2937" : "#9ca3af",
              }}
            >
              {parent
                ? `${parent.name_vietnamese || parent.name} (${parent.code})`
                : "—"}
            </div>
          </div>

          {/* Code */}
          <div>
            <label
              htmlFor="org-create-code"
              style={{
                display: "block",
                fontSize: "13px",
                fontWeight: 500,
                color: "#374151",
                marginBottom: "6px",
              }}
            >
              {t("tenant_admin.org_tree.field_code")}{" "}
              <span style={{ color: "#dc2626" }}>*</span>
            </label>
            <input
              id="org-create-code"
              ref={firstInputRef}
              type="text"
              value={form.code}
              onChange={(e) =>
                setForm((p) => ({ ...p, code: e.target.value.toUpperCase() }))
              }
              placeholder={t("tenant_admin.org_tree.field_code_placeholder") /* HTML attribute */}
              disabled={submitting}
              style={{
                width: "100%",
                padding: "10px 12px",
                border: errors.code ? "1px solid #dc2626" : "1px solid #d1d5db",
                borderRadius: "6px",
                fontSize: "14px",
                minHeight: "44px",
                textTransform: "uppercase",
              }}
              aria-invalid={!!errors.code}
              aria-describedby={errors.code ? "org-create-code-error" : undefined}
            />
            {errors.code ? (
              <p
                id="org-create-code-error"
                style={{ color: "#dc2626", fontSize: "12px", marginTop: "4px" }}
              >
                {errors.code}
              </p>
            ) : (
              <p style={{ color: "#6b7280", fontSize: "12px", marginTop: "4px" }}>
                {t("tenant_admin.org_tree.field_code_hint")}
              </p>
            )}
          </div>

          {/* Name (English) */}
          <div>
            <label
              htmlFor="org-create-name"
              style={{
                display: "block",
                fontSize: "13px",
                fontWeight: 500,
                color: "#374151",
                marginBottom: "6px",
              }}
            >
              {t("tenant_admin.org_tree.field_name")}{" "}
              <span style={{ color: "#dc2626" }}>*</span>
            </label>
            <input
              id="org-create-name"
              type="text"
              value={form.name}
              onChange={(e) => setForm((p) => ({ ...p, name: e.target.value }))}
              placeholder={t("tenant_admin.org_tree.field_name_placeholder") /* HTML attribute */}
              disabled={submitting}
              style={{
                width: "100%",
                padding: "10px 12px",
                border: errors.name ? "1px solid #dc2626" : "1px solid #d1d5db",
                borderRadius: "6px",
                fontSize: "14px",
                minHeight: "44px",
              }}
              aria-invalid={!!errors.name}
              aria-describedby={errors.name ? "org-create-name-error" : undefined}
            />
            {errors.name && (
              <p
                id="org-create-name-error"
                style={{ color: "#dc2626", fontSize: "12px", marginTop: "4px" }}
              >
                {errors.name}
              </p>
            )}
          </div>

          {/* Loại đơn vị (dropdown danh mục — BA-D-010, bắt buộc như SC-006 [10]) */}
          <div>
            <label
              htmlFor="org-create-unit-type"
              style={{
                display: "block",
                fontSize: "13px",
                fontWeight: 500,
                color: "#374151",
                marginBottom: "6px",
              }}
            >
              {t("tenant_admin.org_tree.field_unit_type")}{" "}
              <span style={{ color: "#dc2626" }}>*</span>
            </label>
            <select
              id="org-create-unit-type"
              value={form.unitTypeCode}
              onChange={(e) =>
                setForm((p) => ({ ...p, unitTypeCode: e.target.value }))
              }
              disabled={submitting}
              style={{
                width: "100%",
                padding: "10px 12px",
                border: errors.unitType ? "1px solid #dc2626" : "1px solid #d1d5db",
                borderRadius: "6px",
                fontSize: "14px",
                minHeight: "44px",
                background: "white",
              }}
              aria-invalid={!!errors.unitType}
            >
              <option value="">{t("tenant_admin.org_tree.field_unit_type_placeholder")}</option>
              {unitTypes.map((ut) => (
                <option key={ut.id} value={ut.code}>
                  {ut.name}
                </option>
              ))}
            </select>
            {errors.unitType && (
              <p style={{ color: "#dc2626", fontSize: "12px", marginTop: "4px" }}>
                {errors.unitType}
              </p>
            )}
          </div>


          {/* Submit error */}
          {errors.submit && (
            <div
              role="alert"
              style={{
                padding: "10px 12px",
                background: "#fee2e2",
                border: "1px solid #fca5a5",
                borderRadius: "6px",
                color: "#991b1b",
                fontSize: "13px",
              }}
            >
              {errors.submit}
            </div>
          )}
        </form>

        {/* Footer (sticky actions) */}
        <div
          style={{
            display: "flex",
            justifyContent: "flex-end",
            gap: "8px",
            padding: "16px 24px",
            borderTop: "1px solid #e5e7eb",
            background: "white",
          }}
        >
          <button
            type="button"
            onClick={handleClose}
            disabled={submitting}
            style={{
              padding: "10px 16px",
              background: "white",
              border: "1px solid #d1d5db",
              borderRadius: "6px",
              fontSize: "14px",
              cursor: submitting ? "not-allowed" : "pointer",
              minHeight: "44px",
              minWidth: "88px",
            }}
          >
            {t("tenant_admin.org_tree.button_cancel")}
          </button>
          <button
            type="submit"
            onClick={handleSubmit}
            disabled={submitting}
            style={{
              padding: "10px 16px",
              background: submitting ? "#9ca3af" : "var(--nqh-coffee-500, #d97706)",
              color: "white",
              border: "none",
              borderRadius: "6px",
              fontSize: "14px",
              fontWeight: 500,
              cursor: submitting ? "not-allowed" : "pointer",
              minHeight: "44px",
              minWidth: "88px",
            }}
          >
            {submitting ? "…" : t("tenant_admin.org_tree.button_create")}
          </button>
        </div>
      </div>
    </>
  );
};

export default OrgCreateSidePanel;
